import { useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { erpApi, type CompanyErpStatus as Status } from "../api/erp";

/**
 * Where a customer stands on its way into the ERP: waiting, written with its ERP code, retrying after a
 * failed attempt, or failed with the reason. Shows nothing for a customer that never went to the ERP, or
 * when the status cannot be read: the card itself must not depend on it.
 */
export function CompanyErpStatus({ companyId }: { companyId: string }) {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<Status | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    erpApi.companyStatus(companyId)
      .then((s) => { if (alive) setStatus(s); })
      .catch(() => { if (alive) setStatus(undefined); });
    return () => { alive = false; };
  }, [companyId]);

  if (!status) return null;

  const when = (iso: string) => new Date(iso).toLocaleString(i18n.language);
  const error = readable(status.lastError);
  let text: string;
  let tone: CSSProperties;
  if (status.status === "SENT") {
    text = t("admin.companyDetail.erp.sent", "written to the ERP, code {{code}}", { code: status.erpCode ?? "—" });
    tone = ok;
  } else if (status.status === "FAILED") {
    text = t("admin.companyDetail.erp.failed", "not written to the ERP: {{error}}", { error });
    tone = bad;
  } else if (status.attempts > 0) {
    text = t("admin.companyDetail.erp.retrying", "attempt {{attempts}} failed: {{error}}. Next attempt {{next}}.", {
      attempts: status.attempts, error, next: when(status.nextAttemptAt),
    });
    tone = warn;
  } else {
    text = t("admin.companyDetail.erp.pending", "waiting to be written to the ERP");
    tone = muted;
  }

  return (
    <div style={{ ...box, ...tone }} data-testid="company-erp-status">
      <strong>{t("admin.companyDetail.erp.label", "ERP")}:</strong> {text}
      {status.status === "FAILED" && (
        <div style={{ marginTop: 4, color: "var(--ink-3)" }}>
          {t("admin.companyDetail.erp.failedHint", "After the fix, retry it with “Retry” on the ERP outbox screen.")}
        </div>
      )}
    </div>
  );
}

/**
 * The outbox prefixes its errors with how they ended ("unavailable:", "rejected:"), which the outbox screen
 * shows as is; the card shows only the reason, without the closing full stop the sentence adds itself.
 */
export function readable(lastError: string | null): string {
  if (!lastError) return "—";
  return lastError.replace(/^(unavailable|rejected|erp refused):\s*/i, "").replace(/[.\s]+$/, "") || "—";
}

const box: CSSProperties = { marginTop: 12, padding: "8px 12px", borderRadius: 8, fontSize: 13, border: "1px solid var(--border)" };
const ok: CSSProperties = { background: "var(--ok-bg)", color: "var(--ok)" };
const bad: CSSProperties = { background: "var(--err-bg)", color: "var(--err)" };
const warn: CSSProperties = { background: "var(--warn-bg)", color: "var(--warn)" };
const muted: CSSProperties = { background: "var(--surface-2)", color: "var(--ink-2)" };
